export const buildHourTypesById = (hourTypesGrouped) => {
    const byId = {};
    for (const group of hourTypesGrouped ?? []) {
        for (const ht of group.content ?? []) {
            byId[String(ht.id)] = ht;
        }
        if (group.id && !group.content) {
            byId[String(group.id)] = group;
        }
    }
    return byId;
};

export const hourTypeRequiresValidation = (hourTypeId, hourTypesById) => {
    if (!hourTypeId) return false;
    const ht = hourTypesById[String(hourTypeId)];
    return ht?.is_with_validation === true;
};

export const changeToRequiresValidation = (targetHourTypeId, hourTypesById) =>
    hourTypeRequiresValidation(targetHourTypeId, hourTypesById);

export const formatTimestampToHHmm = (dateInput) => {
    if (!dateInput) return "";
    const date = new Date(dateInput);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (num) => (num < 10 ? "0" + num : num);
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const normalizeTimeToHHmm = (value) => {
    if (!value) return "";
    const str = String(value);
    if (/^\d{2}:\d{2}$/.test(str)) return str;
    return str.slice(0, 5);
};

export const toLocalDayFromInput = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const expectedPlanningStateForDayKey = (lastDatePlanningReal, dayKey) => {
    if (!dayKey) return "PROVISIONAL";

    const [y, m, d] = String(dayKey).split("-").map((v) => parseInt(v, 10));
    if (!y || !m || !d) return "PROVISIONAL";

    const targetDay = new Date(y, m - 1, d);
    if (!lastDatePlanningReal) return "PROVISIONAL";

    const lastDay = toLocalDayFromInput(lastDatePlanningReal);
    if (!lastDay) return "PROVISIONAL";

    return targetDay <= lastDay ? "REAL" : "PROVISIONAL";
};

export const dayEditRequiresValidation = ({ rows, deletedIds, planningById, hourTypesById }) => {
    for (const id of deletedIds ?? []) {
        const ts = planningById.get(String(id));
        if (hourTypeRequiresValidation(ts?.hour_type_id ?? ts?.hour_type?.id, hourTypesById)) {
            return true;
        }
    }

    for (const row of rows ?? []) {
        const targetRequires = hourTypeRequiresValidation(row.hourTypeId, hourTypesById);

        if (!row.tsId) {
            if (targetRequires) return true;
            continue;
        }

        const ts = planningById.get(String(row.tsId));
        if (!ts) {
            if (targetRequires) return true;
            continue;
        }

        const sourceHt = ts.hour_type_id ?? ts.hour_type?.id;
        const sourceRequires = hourTypeRequiresValidation(sourceHt, hourTypesById);
        const hourTypeChanged = String(sourceHt) !== String(row.hourTypeId);
        const timesChanged =
            formatTimestampToHHmm(ts.start_date) !== row.startTime ||
            formatTimestampToHHmm(ts.end_date) !== row.endTime;

        if (!hourTypeChanged && !timesChanged) continue;
        if (sourceRequires || targetRequires) return true;
    }

    return false;
};

export const dayModelRequiresValidation = (dayModelDetails, hourTypesById) =>
    (dayModelDetails ?? []).some((detail) =>
        hourTypeRequiresValidation(detail.hour_type_id ?? detail.hour_type?.id, hourTypesById)
    );

export const buildCorrectionRowsFromDayEdit = (dayEditRows) =>
    (dayEditRows ?? []).map((row) => ({
        hour_type_id: row.hourTypeId,
        start_time: row.startTime,
        end_time: row.endTime,
    }));

export const buildCorrectionRowsFromDayModel = (dayModelDetails) =>
    (dayModelDetails ?? []).map((detail) => ({
        hour_type_id: detail.hour_type_id ?? detail.hour_type?.id,
        start_time: normalizeTimeToHHmm(detail.start_time),
        end_time: normalizeTimeToHHmm(detail.end_time),
    }));

export const buildApplyDayModelTargets = ({
    selectedTimeSlotsDetails,
    selectedUserIdFromSelectedId,
    applyDayModelTargetDayKeys,
    getTimeSlotOwnerId,
    toDayKey,
}) => {
    if ((selectedTimeSlotsDetails ?? []).length > 0) {
        const map = new Map();
        for (const ts of selectedTimeSlotsDetails) {
            const ownerId = getTimeSlotOwnerId(ts);
            const day = toDayKey(ts.start_date);
            if (!ownerId || !day) continue;
            map.set(`${ownerId}|${day}`, { user_id: ownerId, day });
        }
        return Array.from(map.values());
    }

    const userId = selectedUserIdFromSelectedId;
    if (!userId) return [];

    return (applyDayModelTargetDayKeys ?? []).map((day) => ({
        user_id: userId,
        day,
    }));
};
